import { StandardEvents } from '@shopify/events';

const STORAGE_KEY = 'free-shipping-bar-progress';
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const DURATION = 850;

class FreeShippingBar extends HTMLElement {
  static get observedAttributes() {
    return ['data-progress'];
  }

  /** @type {number} */
  #timer = 0;

  connectedCallback() {
    const stored = parseFloat(sessionStorage.getItem(STORAGE_KEY) || '');
    const from = Number.isFinite(stored) ? stored : 0;
    this.animateProgress(from, this.targetProgress);
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-progress' || oldValue == null || oldValue === newValue) return;
    this.animateProgress(this.visualProgress, this.targetProgress);
  }

  /**
   * @returns {number}
   */
  get targetProgress() {
    return clamp(parseFloat(this.dataset.progress || '0') || 0);
  }

  /**
   * @returns {number}
   */
  get visualProgress() {
    const fill = this.querySelector('.free-shipping-bar__fill');
    const track = this.querySelector('.free-shipping-bar__track');
    if (!(fill instanceof HTMLElement) || !(track instanceof HTMLElement)) return this.targetProgress;

    const total = track.getBoundingClientRect().width;
    if (!total) return this.targetProgress;

    return clamp((fill.getBoundingClientRect().width / total) * 100);
  }

  /**
   * @param {number} [from]
   * @param {number} [to]
   */
  animateProgress(from = this.visualProgress, to = this.targetProgress) {
    const fill = this.querySelector('.free-shipping-bar__fill');
    if (!(fill instanceof HTMLElement)) return;

    const start = clamp(from);
    const end = clamp(to);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    fill.style.transition = 'none';
    fill.style.width = `${start}%`;
    void fill.offsetWidth;

    if (reduced || Math.abs(end - start) < 0.15) {
      fill.style.width = `${end}%`;
      sessionStorage.setItem(STORAGE_KEY, String(end));
      this.classList.remove('is-animating');
      return;
    }

    this.classList.add('is-animating');
    fill.style.transition = `width ${DURATION}ms ${EASE}`;
    fill.style.width = `${end}%`;
    sessionStorage.setItem(STORAGE_KEY, String(end));

    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.classList.remove('is-animating'), DURATION);
  }
}

/**
 * @param {number} value
 * @returns {number}
 */
function clamp(value) {
  return Math.max(0, Math.min(100, value));
}

function bindCartProgressAnimation() {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /** @type {{ bar: FreeShippingBar, from: number }[]} */
    const snapshots = [];

    document.querySelectorAll('free-shipping-bar').forEach((bar) => {
      if (bar instanceof FreeShippingBar) {
        snapshots.push({ bar, from: bar.visualProgress });
      }
    });

    event.promise
      ?.then(() => {
        window.setTimeout(() => {
          document.querySelectorAll('free-shipping-bar').forEach((bar) => {
            if (!(bar instanceof FreeShippingBar)) return;
            const snapshot = snapshots.find((item) => item.bar === bar);
            bar.animateProgress(snapshot?.from ?? bar.visualProgress, bar.targetProgress);
          });
        }, 0);
      })
      .catch(() => {});
  });
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

if (!window.__freeShippingBarCartBound) {
  window.__freeShippingBarCartBound = true;
  bindCartProgressAnimation();
}
